import type { PromptResult } from '@pn/core/operations/gpt/promptToWorkspaceItem';
import { generateId } from '@pn/core/utils/id';
import { isArray } from 'lodash-es';
import React from 'react';

/* Context */

type ChatMessagePayload =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: PromptResult | string }
  | { role: 'system'; content: string };

export type ChatMessage = {
  id: string;
} & ChatMessagePayload;

type ChatMessageParams = ChatMessagePayload & {
  id?: ChatMessage['id'];
};

type ChatAction =
  | { type: 'ADD_MESSAGES'; messages: ChatMessage[] }
  | { type: 'CLEAR_MESSAGES' };

type ChatContextType = {
  messages: ChatMessage[];
  addMessages: (arg: ChatMessageParams | ChatMessageParams[]) => void;
  clearMessages: () => void;
};

const ChatContext = React.createContext<ChatContextType | undefined>(undefined);

/* Reducer and provider */

function chatReducer(state: ChatMessage[], action: ChatAction): ChatMessage[] {
  switch (action.type) {
    case 'ADD_MESSAGES':
      return [...state, ...action.messages];
    case 'CLEAR_MESSAGES':
      return [];
    default:
      return state;
  }
}

function addId(message: ChatMessageParams): ChatMessage {
  return { ...message, id: message.id ?? generateId() };
}

type Props = {
  children: React.ReactNode;
};

export const ChatProvider = ({ children }: Props) => {
  const [messages, dispatch] = React.useReducer(chatReducer, []);

  const addMessages = (arg: ChatMessageParams | ChatMessageParams[]) =>
    dispatch({
      type: 'ADD_MESSAGES',
      messages: isArray(arg) ? arg.map(addId) : [addId(arg)],
    });

  const clearMessages = () => dispatch({ type: 'CLEAR_MESSAGES' });

  return (
    <ChatContext.Provider value={{ messages, addMessages, clearMessages }}>
      {children}
    </ChatContext.Provider>
  );
};

export function useChat() {
  const context = React.useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
}
